import { useContext, useState, useEffect } from 'react';
import { gql } from '@apollo/client';

import {
	Issue,
	getAppToken,
	getUserRole,
	getImagesFromIssue,
	clone,
	mapIssuesNonImplementedFeatures,
} from '../constants';
import { AppContext } from '../App.context';
import { Db } from '../services/Db';
import { KV } from '../services/kv/KV';
import config from '../config';
import { useNavigate } from 'react-router-dom';

/*
	Redirect to base url if the issue is not found
*/

export function useIssueNotFound(issue?: Issue | null) {
	const navigate = useNavigate();
	const { baseUrl } = useContext(AppContext);
	useEffect(() => {
		if (issue === null) {
			navigate(baseUrl, { replace: true });
		}
	}, [issue]);
}

export function useGetIssues(): GetIssuesResponse {
	const { appKey, apolloClient, onlineStatus, db, kv, sw } =
		useContext(AppContext);
	const [issues, setIssues] = useState<Array<Issue>>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	useEffect(() => {
		if (!apolloClient) {
			return;
		}
		if (onlineStatus === null) {
			return;
		}
		if (isLoading === false) {
			return;
		}
		if (!db) {
			return;
		}

		const props = {
			apolloClient,
			appKey,
			onlineStatus,
			sw,
			db,
			kv,
		};

		getIssues(props)
			.then((issues) => {
				setIssues(issues);
			})
			.catch((err) => {
				console.error(err);
				setError(err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [apolloClient, onlineStatus, isLoading, sw, db]);
	return {
		issues,
		isLoading,
		error,
	};
}

export async function getIssues(args: GetIssuesArgs): Promise<Array<Issue>> {
	const { onlineStatus, db } = args;
	/*const issues = await getRemoteIssues(args);
	return issues.sort(sortReleaseDate);*/
	if (onlineStatus) {
		const issues = await getRemoteIssues(args);
		console.log(`ALL ISSUES`, issues);
		//kv.set('issues', clone(issues), null);
		// await db.saveIssues(issues);
		return issues.sort(sortIssuesReleaseDate);
	}

	return (await db.getIssues()).sort(sortIssuesReleaseDate);
}

export function sortIssuesReleaseDate(a: Issue, b: Issue) {
	if (a.releaseDate && b.releaseDate) {
		return (
			new Date(`${b.releaseDate}`).getTime() -
			new Date(`${a.releaseDate}`).getTime()
		);
	}
	return (
		new Date(`${b.updated}`).getTime() - new Date(`${a.updated}`).getTime()
	);
}

async function getRemoteIssues(args: GetIssuesArgs) {
	const { apolloClient, appKey, sw, kv } = args;
	const fetchPolicyKey = 'remote-issues';

	const fetchPolicy = kv.get(fetchPolicyKey, 'no-cache');
	if (!kv.has(fetchPolicyKey)) {
		kv.set(
			fetchPolicyKey,
			'cache-first',
			config?.app?.cache?.ttl?.getAllIssues ?? undefined,
		);
	}

	const headers = {
		'app-key': appKey,
		Authorization: getAppToken(),
		'app-user-role': getUserRole(),
	};

	const result = await apolloClient.query({
		query: GET_ISSUES,
		fetchPolicy,
		headers,
	});

	let { issues } = clone(result.data);
	if (issues['__typename']) {
		delete issues['__typename'];
	}

	// TODO Remove this to support features
	issues = issues.map(mapIssuesNonImplementedFeatures);

	if (sw) {
		const images: Set<string> = new Set();
		getImagesFromIssue(images, issues);
		if (images.size) {
			sw.postMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				payload: {
					images: [...images],
				},
			});
		}
	}

	return issues;
}

export interface GetIssuesArgs {
	appKey: string;
	apolloClient: any;
	onlineStatus: boolean;
	sw: ServiceWorker | null;
	db: Db;
	kv: KV;
}

export interface GetIssuesResponse {
	issues: Array<Issue>;
	isLoading: boolean;
	error?: Error | null;
}

export const GET_ISSUES = gql`
	{
		issues {
			id
			name
			description
			cover
			externalUrl
			price
			index
			slug
			currency
			data
			updated
			created
			isPurchased
			releaseDate
			articles {
				id
				name
				slug
				index
				isFeatured
				created
				updated
			}
			featuredArticles {
				id
				name
				thumbnail
				channel
				keywords
				featureImage
				slug
				isFeatured
				created
				updated
			}
		}
	}
`;

// TODO Use this request to revert back
export const HACK_GET_ISSUES = gql`
	{
		issues {
			id
			name
			description
			cover
			externalUrl
			price
			index
			slug
			currency
			data
			features
			updated
			created
			isPurchased
			releaseDate
			articles {
				id
				name
				slug
				index
				isFeatured
				created
				updated
			}
			featuredArticles {
				id
				name
				thumbnail
				channel
				keywords
				featureImage
				slug
				isFeatured
				created
				updated
			}
		}
	}
`;
